import { useRef, useEffect } from 'react'
import { useQuery as useQueryOriginal } from '@apollo/client'
import { fetchPolicy as globalFetchPolicy } from 'config/graphql'
import { transformData } from '../utils'
import useFetchMoreState from '../useFetchMoreState'
import produce from 'immer'
import set from 'lodash/set'
import get from 'lodash/get'
import { LIST_QUERY_LIMIT } from 'config/consts'

const useListQuery = (query, options = {}) => {
	const { queryRef, fetchPolicy, variables: customVars = {}, sort, search, filter, limit = LIST_QUERY_LIMIT, cursorsLimitOnly, onCompleted, skip, startFromId, clearStartFromId, onError } = options
	const [fetchMoreState] = useFetchMoreState()
	const startFromIdInitial = useRef(startFromId)
	if(clearStartFromId) {
		startFromIdInitial.current = startFromId
	}

	const queryVariables = {
		...customVars,
		params: {
			...!cursorsLimitOnly && { limit },
			sort,
			cursors: {
				limit,
				...startFromIdInitial.current && {
					before: Buffer.from(`[${startFromIdInitial.current}]`).toString('base64'),
					after: Buffer.from(`[${startFromIdInitial.current}]`).toString('base64')
				}
			},
			...((search && search.string) && { search })
		},
		filter: {
			...filter,
			...customVars.filter
		}
	}

	/*
		fetch data from cache as separate `useQuery`
		https://github.com/apollographql/react-apollo/issues/3333
	*/
	const { data: dataFromCache } = useQueryOriginal(query, {
		errorPolicy: 'all',
		fetchPolicy: 'cache-only',
		variables: queryVariables
	})
	const queryResult = useQueryOriginal(query, {
		fetchPolicy: fetchPolicy || globalFetchPolicy,
		errorPolicy: 'all',
		notifyOnNetworkStatusChange: true,
		variables: produce(queryVariables, draft => {
			draft.params.offset = 0
		}),
		...(typeof onCompleted === 'function' && { onCompleted }),
		...(typeof onError === 'function' && { onError }),
		skip
	})

	const useDataFromCache = dataFromCache && Object.keys(dataFromCache).length && (!queryResult.data || !Object.keys(queryResult.data).length)
	const result = {
		...queryResult,
		data: useDataFromCache ? dataFromCache : queryResult.data
	}

	if(queryRef) {
		queryRef.current = result
	}

	const { data, fetchMore, variables, ...rest } = fetchMoreState(result)
	const { data: transformedData, ...dataInfo } = transformData(data, { queryFieldExtract: true })

	let out = {
		...rest,
		...dataInfo,
		withData: true,
		queryVariables,
		data: []
	}

	if(transformedData) {
		const { items = [], hasNextPage, cursors } = transformedData
		const isDataConnection = !Array.isArray(transformedData)
		if(!rest.error && !rest.loading && isDataConnection && typeof hasNextPage === 'undefined' && typeof cursors.hasNext === 'undefined') {
			throw new Error(`Connection ${transformedData.__typename} did not load 'hasNextPage' and 'cursors.hasNext' flags`)
		}
		const clientData = isDataConnection ? items : transformedData
		const { params } = variables

		if(startFromIdInitial.current) {
			out.key = startFromIdInitial.current
		}

		out = {
			...out,
			data: clientData,
			hasNextPage: cursors ? cursors.hasNext : hasNextPage,
			hasPreviousPage: cursors && cursors.hasPrevious,
			loadNextPage: () => fetchMore({
				variables: cursors ? {
					params: {
						...params,
						cursors: {
							after: cursors.after,
							limit
						}
					}
				} : {
					params: {
						...params,
						offset: items.length
					}
				},
				updateQuery: (prev = {}, { fetchMoreResult }) => {
					if (!fetchMoreResult) {
						return prev
					}
					const { data: newData, appField, queryField } = transformData(fetchMoreResult, { queryFieldExtract: true })
					const objPath = appField ? `${appField}.${queryField}` : queryField

					/* in a case when we have multiple components which use list query on one page requests is sent only once,
					   but updateQuery will fire multiple times. This check ensures that we write result to cache only once */
					if(get(prev, `${objPath}.cursors.after`) === get(fetchMoreResult, `${objPath}.cursors.after`)) {
						return prev
					}
					const updatedResult = produce(prev, draft => {
						const newItems = [...get(prev, `${objPath}.items`, []), ...newData.items]
						set(draft, objPath, { ...newData, items: newItems })
						if(cursors) {
							set(draft, `${objPath}.cursors`, {
								...newData.cursors,
								before: cursors.before,
								hasPrevious: cursors.hasPrevious
							})
						}
					})
					return updatedResult
				}
			}),
			...!!cursors && { loadPreviousPage: () => fetchMore({
				variables: {
					params: {
						...params,
						cursors: {
							before: cursors.before,
							limit
						}
					}
				},
				updateQuery: (prev = {}, { fetchMoreResult }) => {
					if (!fetchMoreResult) {
						return prev
					}
					const { data: newData, appField, queryField } = transformData(fetchMoreResult, { queryFieldExtract: true })
					const objPath = appField ? `${appField}.${queryField}` : queryField

					/* in a case when we have multiple components which use list query on one page requests is sent only once,
					   but updateQuery will fire multiple times. This check ensures that we write result to cache only once */
					if(get(prev, `${objPath}.cursors.before`) === get(fetchMoreResult, `${objPath}.cursors.before`)) {
						return prev
					}
					const updatedResult = produce(prev, draft => {
						const newItems = [...newData.items, ...get(prev, `${objPath}.items`, [])]
						set(draft, objPath, { ...newData, items: newItems })
						if(cursors) {
							set(draft, `${objPath}.cursors`, {
								...newData.cursors,
								after: cursors.after,
								hasNext: cursors.hasNext
							})
						}
					})
					return updatedResult
				}
			}) }
		}
	}

	useEffect(() => {
		// some queries from dynamoDB which use filter expression do not guarantee to return
		// the exact number of records specified in `limit`, so here we query for more
		// if there is the next page available
		if(transformedData && out.hasNextPage && out.data.length < limit && !rest.loading) {
			out.loadNextPage()
		}
	})

	return out
}

export default useListQuery
